/**
 * i18n — initialisation d'i18next pour l'application.
 *
 * PRINCIPES
 *
 * 1. Le français est TOUJOURS embarqué. C'est la langue de repli, et le repli
 *    se fait clé par clé : une langue traduite à moitié affiche le reste en
 *    français plutôt que des identifiants techniques. C'est ce qui autorise à
 *    traduire une zone à la fois.
 *
 * 2. Les autres langues se chargent à la demande, par espace de noms. Le
 *    catalogue complet dépassera les 20 000 mots : le charger en entier au
 *    démarrage ferait payer à chacun les écrans qu'il n'ouvrira pas.
 *
 * 3. `escapeValue: false`. i18next échappe ses interpolations pour du HTML
 *    brut ; React échappe déjà tout ce qu'il affiche. Laisser les deux donne
 *    des `&#39;` à l'écran.
 *
 * 4. Pas de Suspense. `useSuspense: false` : l'application n'a pas de frontière
 *    Suspense à sa racine, et une langue qui met 200 ms à arriver ne justifie
 *    pas d'en poser une. Le temps du chargement, i18next rend le français.
 *
 * Le catalogue `courriels` n'est PAS declare ici : il est lu par PHP
 * (`serveur/i18n/traduire.php`) et par lui seul. Le digest quotidien part a 7h,
 * sans navigateur ; ces chaines n'ont rien a faire dans le bundle.
 *
 * Les pluriels passent par `Intl.PluralRules` (i18next v21+), seul moyen d'avoir
 * les six formes de l'arabe et les quatre du russe. Les 62 accords écrits à la
 * main dans le dépôt (`n > 1 ? 'élèves' : 'élève'`) sont à reprendre en
 * `t('cle', { count: n })` au fil de l'extraction.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { LANGUE_PAR_DEFAUT, CODES_DECLARES } from './languesInterface';
import { appliquerLangueAuDocument } from './appliquerLangueAuDocument';
import { resoudreLangue } from './detection';

import communFr from './locales/fr/commun.json';
import appFr from './locales/fr/app.json';
import erreursFr from './locales/fr/erreurs.json';
import siteFr from './locales/fr/site.json';
import adminFr from './locales/fr/admin.json';
import modulesFr from './locales/fr/modules.json';
import constructeurFr from './locales/fr/constructeur.json';
import chatFr from './locales/fr/chat.json';
import suiviFr from './locales/fr/suivi.json';
import eleveFr from './locales/fr/eleve.json';

/**
 * Les espaces de noms, un par zone de l'application.
 *
 * Ajouter une zone = créer `locales/<langue>/<zone>.json` pour CHAQUE langue
 * déclarée (un fichier `{}` suffit tant qu'elle n'est pas traduite) et ajouter
 * son nom ici. Le fichier doit exister partout : le chargeur ci-dessous
 * construit son chemin, et une absence deviendrait une erreur de résolution.
 */
export const ESPACES = ['commun', 'erreurs', 'app', 'site', 'admin', 'modules', 'constructeur', 'chat', 'suivi', 'eleve'];

export const ESPACE_PAR_DEFAUT = 'commun';

/** Les catalogues français, embarqués. */
const ressourcesFr = {
    commun: communFr,
    app: appFr,
    erreurs: erreursFr,
    site: siteFr,
    admin: adminFr,
    modules: modulesFr,
    constructeur: constructeurFr,
    chat: chatFr,
    suivi: suiviFr,
    eleve: eleveFr,
};

i18next
    .use(initReactI18next)
    .init({
        lng: resoudreLangue(),
        fallbackLng: LANGUE_PAR_DEFAUT,
        supportedLngs: CODES_DECLARES,

        ns: ESPACES,
        defaultNS: ESPACE_PAR_DEFAUT,

        resources: {
            [LANGUE_PAR_DEFAUT]: ressourcesFr,
        },

        interpolation: {
            escapeValue: false, // React échappe déjà.
        },

        react: {
            useSuspense: false,
        },

        // Une clé absente rend la valeur française grâce à `fallbackLng`. Si
        // elle manque AUSSI en français, c'est un défaut : on rend la clé, qui
        // se voit, plutôt qu'une chaîne vide, qui ne se voit pas.
        returnEmptyString: false,

        debug: false,
    });

/** Les catalogues déjà chargés, pour ne pas les redemander. `fr` y est d'emblée. */
const dejaCharges = new Set(ESPACES.map((ns) => `${LANGUE_PAR_DEFAUT}:${ns}`));

/**
 * Charge les catalogues d'une langue, espace par espace.
 *
 * Un espace qui échoue n'interrompt pas les autres : il restera en français,
 * ce qui est dégradé mais lisible. Une panne de réseau sur un fragment ne doit
 * pas laisser l'application sans interface.
 */
export const chargerLangue = async (code) => {
    if (code === LANGUE_PAR_DEFAUT) return;

    await Promise.all(ESPACES.map(async (ns) => {
        const marque = `${code}:${ns}`;
        if (dejaCharges.has(marque)) return;
        try {
            const module = await import(`./locales/${code}/${ns}.json`);
            i18next.addResourceBundle(code, ns, module.default || module, true, true);
            dejaCharges.add(marque);
        } catch (e) {
            // Catalogue absent ou injoignable : le repli français prend le
            // relais. On le DIT : un chargement muet qui echoue rend une
            // interface entierement francaise sous un drapeau etranger, et
            // c'est indiscernable d'une traduction manquante.
            // eslint-disable-next-line no-console
            console.error(`[i18n] catalogue ${code}/${ns} introuvable :`, e && e.message);
        }
    }));
};

/**
 * Le catalogue de la langue resolue est-il pret ?
 *
 * POURQUOI CETTE PROMESSE EXISTE
 *
 * i18next est initialise avec le SEUL francais : les autres catalogues se
 * chargent a la demande, pour ne pas faire payer a chacun les langues qu'il
 * n'ouvrira pas. Il faut donc charger celui de la langue resolue AVANT le
 * premier rendu.
 *
 * Ce n'etait pas fait, et le defaut ne se voyait pas dans les tests : la
 * langue basculait bien (`<html lang="en">`, le selecteur affichait English),
 * mais aucune ressource anglaise n'existait, si bien que TOUT retombait sur le
 * repli francais. L'ecran ne changeait pas d'un mot.
 *
 * `src/index.js` attend cette promesse avant de monter React. Elle est deja
 * resolue quand la langue est le francais : rien n'est retarde dans le cas
 * courant.
 */
export const pretI18n = chargerLangue(i18next.language || LANGUE_PAR_DEFAUT);

// Au démarrage, le document doit porter la langue résolue : sans cela, la
// première peinture se fait en `lang="fr"` et l'arabe s'affiche de gauche à
// droite le temps du premier rendu.
appliquerLangueAuDocument(i18next.language || LANGUE_PAR_DEFAUT);

export default i18next;
